import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Download, Mail } from 'lucide-react';
import { logger } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val).replace(/"/g, '""');
  return /[",\n;]/.test(s) ? `"${s}"` : s;
};

const NewsletterAdmin = ({ token }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erreur chargement newsletter');
      const data = await res.json();
      setSubs(data);
    } catch (err) {
      logger.warn('Newsletter list error', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchSubs();
  }, [token, fetchSubs]);

  const exportCsv = () => {
    const rows = [
      ['date', 'email', 'source'],
      ...subs.map((s) => [
        s.created_at?.slice(0, 19).replace('T', ' ') || '',
        s.email,
        s.source || '',
      ]),
    ];
    const csv = rows.map((r) => r.map(csvEscape).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sourceCounts = subs.reduce((acc, s) => {
    const k = s.source || 'inconnu';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div
      className="bg-[#302f2c] border border-[#3f4816] rounded-xl overflow-hidden mb-8"
      data-testid="newsletter-admin"
    >
      <div className="p-4 border-b border-[#3f4816] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-[#d9fb06]/15 text-[#d9fb06] rounded-full p-2">
            <Mail size={18} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#d9fb06]">
              Newsletter ({subs.length} inscrit{subs.length > 1 ? 's' : ''})
            </h2>
            <p className="text-xs text-[#888680] mt-0.5">
              {Object.entries(sourceCounts)
                .map(([k, n]) => `${n} via ${k}`)
                .join(' · ') || 'Aucun inscrit pour le moment'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={subs.length === 0}
          className="flex items-center gap-2 border border-[#d9fb06] text-[#d9fb06] px-3 py-2 rounded-lg text-sm hover:bg-[#d9fb06] hover:text-[#1a1c1b] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="newsletter-export-csv"
        >
          <Download size={16} />
          Exporter CSV
        </button>
      </div>
      {loading ? (
        <div className="p-4 flex items-center text-[#888680] text-sm">
          <Loader2 size={16} className="animate-spin mr-2" />
          Chargement…
        </div>
      ) : subs.length === 0 ? null : (
        <div className="p-4 max-h-48 overflow-y-auto text-sm">
          <ul className="divide-y divide-[#3f4816]">
            {subs.slice(0, 50).map((s) => (
              <li
                key={s.email}
                className="py-2 flex items-center justify-between gap-3"
              >
                <span className="text-[#dfddd6] truncate">{s.email}</span>
                <span className="text-[#888680] text-xs whitespace-nowrap">
                  {s.created_at?.slice(0, 10)} · {s.source}
                </span>
              </li>
            ))}
          </ul>
          {subs.length > 50 && (
            <p className="text-[#888680] text-xs mt-2 text-center">
              Affichage des 50 plus récents — exporte en CSV pour la liste complète.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsletterAdmin;
